import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLazyListUserDeliveryPricesForReportsQuery } from "store/services/reportApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import ExportUserCODReport from "./ExportUserCODReport";
import { BaseTabs } from "components/Shared/BaseTabs";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  { title: "COD User Report" },
];

const chargeTypeTabs = [
  { label: "All", value: "All" },
  { label: "Charge", value: "Charge" },
  { label: "COD", value: "Cash on Delivery" },
];

const UserDeliveryCollectionReport = () => {

  const [searchParams, setSearchParams] = useSearchParams();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { viewEntity } = useAuth();

  const [responsibleUser, setResponsibleUser] = useState(
    searchParams.get("responsibleUser") || "all"
  );
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "All"
  );

  const [
    fetchShipmentPrices,
    { data: priceRes, isLoading, isFetching, error },
  ] = useLazyListUserDeliveryPricesForReportsQuery();

  const [
    fetchUsers,
    { data: usersData, isLoading: isUsersLoading, isFetching: isUserFetching },
  ] = useLazyGetDDLUserListQuery();

    // Sync state with URL params
      useEffect(() => {
          const tabFromUrl = searchParams.get("tab");
          const userFromUrl = searchParams.get("responsibleUser");
  
          if (tabFromUrl && tabFromUrl !== activeTab) {
              setActiveTab(tabFromUrl);
          }
          if (userFromUrl && userFromUrl !== responsibleUser) {
              setResponsibleUser(userFromUrl);
          }
      }, [activeTab, responsibleUser, searchParams]);

  const columns = [
    {
      name: "Name",
      selector: (row) => row.responsibleUserName,
      cell: (row) => (
        <Link
          title={row?.responsibleUserName}
          to={`/app/crm/reports/delivery-report/user/${row?._id}/detail?chargeType=${activeTab}`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.responsibleUserName ? (
            <HighlightedText
              text={row.responsibleUserName}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Pending",
      selector: (row) => row?.totalNotPaid || 0,
      cell: (row) =>
        row?.totalNotPaid ? `Rs.${Math.abs(row.totalNotPaid).toFixed(2)}` : 0,
      sortable: true,
      align: "right",
    },
    {
      name: "Requested",
      selector: (row) => row?.totalPaid || 0,
      cell: (row) =>
        row?.totalPaid ? `Rs. ${Math.abs(row.totalPaid).toFixed(2)}` : 0,
      sortable: true,
      align: "right",
    },
    {
      name: "Finance Collected",
      selector: (row) => row?.totalCollected || 0,
      cell: (row) =>
        row?.totalCollected
          ? `Rs. ${Math.abs(row.totalCollected).toFixed(2)}`
          : 0,
      sortable: true,
      align: "right",
    },
    {
      name: "Total",
      selector: (row) => row?.total || 0,
      cell: (row) =>
        row?.total ? `Rs. ${Math.abs(row.total).toFixed(2)}` : "N/A",
      sortable: true,
      align: "right",
    },
  ];

  useEffect(() => {
    fetchUsers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [fetchUsers, viewEntity]);

  const userOptions = [
    { label: "All", value: "all" },
    ...(usersData?.map((user) => ({
      label: user.label || user.name,
      value: user.value || user._id,
    })) || []),
  ];

  useEffect(() => {
    const queryParams = {
      responsibleUser: responsibleUser === "all" ? undefined : responsibleUser,
      chargeType: activeTab,
      limit: perPage,
      page: currentPage,
    };
    console.log("Fetching shipment prices with params:", queryParams);
    fetchShipmentPrices(queryParams, { preferCacheValue: false })
      .unwrap()
      .then(() => console.log("API call successful"))
      .catch((err) => console.error("API call failed:", err));
  }, [currentPage, fetchShipmentPrices, perPage, responsibleUser, activeTab]);

  const handleTabChange = (newTab) => {
    console.log("Tab changed to:", newTab);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", newTab);
      params.set("page", "1"); // Reset to page 1 on tab change
      return params;
    });
  };

  return (
    <div>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ minWidth: 180 }}>
                COD User Report
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                <Autocomplete
                  size="small"
                  options={userOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={
                    userOptions.find(
                      (opt) => opt.value === responsibleUser
                    ) || {
                      value: "all",
                      label: "All Users",
                    }
                  }
                  onChange={(e, newValue) => {
                    const newValueSelected = newValue?.value || "all";
                    setResponsibleUser(newValueSelected);
                    setSearchParams((prev) => {
                      const params = new URLSearchParams(prev);
                      params.set("responsibleUser", newValueSelected);
                      params.set("page", "1"); // Reset to page 1
                      return params;
                    });
                  }}
                  disableClearable
                  disabled={isUsersLoading || isUserFetching}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Responsible User" />
                  )}
                />
                <ExportUserCODReport
                  responsibleUser={responsibleUser}
                  chargeType={activeTab}
                />
              </Stack>
            </Box>

            <BaseTabs
              tabs={chargeTypeTabs}
              activeTab={activeTab}
              // actions={<ExportUserCODReport />}
              onTabChange={handleTabChange}
            />

            <Divider sx={{ mb: 2 }} />

            {isLoading || isFetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : priceRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters
                </Typography>
              </Box>
            ) : (
              <AppTable
                columns={columns}
                data={priceRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={priceRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )}
          </Box>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </div>
  );
};

export default UserDeliveryCollectionReport;
